"use client";

import { motion } from "framer-motion";
import { X, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";

const NotificationModal = ({ onClose, isOpen }) => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Disable background scroll when modal is open, enable when closed
  useEffect(() => {
    if (isOpen === true) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Fetch announcements on modal open
  useEffect(() => {
    async function fetchAnnouncements() {
      if (!isOpen) return;

      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/announcements/fetch-announcements`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch announcements");
        }

        const data = await response.json();
        setNotifications(data);
        setError(null); // Clear any previous errors
      } catch (err) {
        console.error("Error fetching announcements:", err);
        setError("Failed to load notifications");
      } finally {
        setIsLoading(false);
      }
    }

    fetchAnnouncements();
  }, [isOpen]);

  const categories = [
    "all",
    ...new Set(notifications.map((n) => n.category).filter(Boolean)),
  ];

  const filteredNotifications =
    activeCategory === "all"
      ? notifications
      : notifications.filter((n) => n.category === activeCategory);

  // Prevent rendering if modal is closed or no notifications (after loading)
  if (!isOpen || (!isLoading && notifications.length === 0)) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="relative w-[950px] h-[605px] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with close button */}
        <div className="bg-gradient-to-r from-blue-800 to-blue-900 p-3 sm:p-4 flex items-center">
          {filteredNotifications.length > 0 &&
            filteredNotifications[0].title && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-2">
                <h2 className="text-lg sm:text-xl font-bold text-white line-clamp-1">
                  {filteredNotifications[0].title}
                </h2>
                {filteredNotifications[0].link && (
                  <div className="flex justify-between items-center p-1 sm:p-1">
                    {/* Notification Button */}
                    <a
                      href={filteredNotifications[0].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-bold bg-blue-700 text-white px-3 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      {filteredNotifications[0].button_name ? (
                        <span>{filteredNotifications[0].button_name}</span>
                      ) : (
                        <span>Learn More</span>
                      )}
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </a>

                    {/* Close Button */}
                    <button
                      onClick={onClose}
                      className="text-white hover:text-gray-300 transition-colors bg-black/30 hover:bg-black/40 p-2 rounded-full ml-2"
                      aria-label="Close modal"
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Category filters */}
        {categories.length > 1 && (
          <div className="flex overflow-x-auto px-3 sm:px-4 py-2 bg-gray-50 border-b gap-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                  activeCategory === category
                    ? "bg-blue-100 text-blue-800 font-medium"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-0 sm:p-0 md:p-0">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
            </div>
          ) : filteredNotifications.length > 0 ? (
            <div className="space-y-4">
              {filteredNotifications.map((notification, index) => (
                <div
                  key={index}
                  className={`rounded-r-lg transition-all hover:shadow-md ${
                    notification.priority === "high"
                      ? "border-red-500 bg-red-50"
                      : notification.priority === "medium"
                      ? "border-yellow-500 bg-yellow-50"
                      : "bg-blue-50"
                  }`}
                >
                  {/* Image container */}
                  <div className="relative w-full">
                    <img
                      src="/images/home-page/adminssion-notification.jpg"
                      alt="Announcement"
                      height="100%"
                      width="100%"
                      className="object-contain rounded-md mx-auto"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No announcements available</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NotificationModal;
